/**
 * The Bench half of the API surface.
 *
 * Every call goes through `@/lib/api`, which owns the cookie, the Idempotency-Key on every
 * mutation, RFC 9457 problem parsing and the step-up retry. Nothing here talks to `fetch`.
 *
 * Vial label jobs are the only writes of their own. Reprints and address edits reuse the
 * Orders feature's calls (`ordersApi.reprint` via `reprintMany`, `ordersApi.editAddress`),
 * so there is exactly one client for each of those routes in the product.
 */
import type { Page } from '@elutide/shared';
import { api } from '@/lib/api';
import type {
  BenchOrderRow,
  VialEnqueueRequest,
  VialJob,
  VialJobsResponse,
  VialProductsResponse,
} from './types';

export const benchApi = {
  /** Every product with a current lot, plus when the label printer agent last polled. */
  products: () => api.get<VialProductsResponse>('/bench/vial/products'),

  /** Newest first. */
  jobs: (limit = 50) => api.get<VialJobsResponse>('/bench/vial/jobs', { query: { limit } }),

  /**
   * Queue a print. The Idempotency-Key is the job's `source_ref`, so a retry after a
   * timeout returns the same job instead of printing a second run. The key is minted by
   * the button press and held across that press's retries, never regenerated inside here.
   */
  enqueue: (body: VialEnqueueRequest, idempotencyKey: string) =>
    api.post<{ job: VialJob }>('/bench/vial/jobs', body, { idempotencyKey }),

  /** Only a queued job can be cancelled. Anything else is a 409 the screen shows verbatim. */
  cancel: (jobId: number) => api.post<{ job: VialJob }>(`/bench/vial/jobs/${jobId}/cancel`),

  /**
   * Find an order by its number. The existing search route answers with the same rows the
   * Orders list uses, projected for the role, so a packer's row has no money in it.
   */
  findOrders: (q: string) =>
    api.get<Page<BenchOrderRow>>('/orders/search', { query: { q, limit: 10 } }),
};

/** Normalise what the operator typed: "#12345 " and "12345" are the same order. */
export function normaliseOrderNumber(raw: string): string {
  return raw.trim().replace(/^#/, '').trim();
}

/**
 * Pick the row that matches the typed number. An exact match on `order_number` wins; a
 * single result is accepted as the answer; anything else is "not found" rather than a
 * guess, because a reprint for the wrong order is a label on the wrong box.
 */
export function pickOrder(rows: readonly BenchOrderRow[], number: string): BenchOrderRow | null {
  const exact = rows.find((row) => row.order_number === number);
  if (exact) return exact;
  return rows.length === 1 ? (rows[0] ?? null) : null;
}
